/**
 * Themed colour picker for the cart upsell card.
 *
 * WHY THIS EXISTS. The card shipped a native <select>. A browser draws that
 * control's OPEN list itself -- it is an OS menu, not part of the page -- so no
 * stylesheet can reach it, and it landed as a grey system list over the card.
 * The rake PDP hit the same wall and solved it the same way: build the listbox.
 *
 * WHY THE LIST MOVES TO <body>. The card sits inside a splide track, which has
 * to clip its overflow to work as a carousel. Any descendant of that track is
 * clipped with it, which cut the open list off after two rows. z-index does not
 * help -- clipping by an ancestor's overflow ignores stacking entirely. So on
 * open the list is moved out to <body> and positioned in viewport coordinates
 * against the trigger, and on close it is put back where it came from.
 *
 * WHAT DRIVES WHAT. The native select stays in the DOM, hidden. It is still the
 * source of truth: choosing an option writes select.value and fires one change
 * event, which is what the theme's own handler (cart-product-upsell.js) listens
 * for to update the hidden variant id, the price and the compare-at price, and
 * what chw-upsell-image.js listens for to swap the card image. This file owns
 * the presentation and nothing else, so if it never loads the card still works.
 *
 * WHY DELEGATED. The drawer replaces its own innerHTML on every cart change,
 * which would throw away listeners bound to the card, and scripts inside
 * replaced HTML never execute. Everything here hangs off document instead, and
 * the markup ships from Liquid already in its correct initial state, so there
 * is nothing to re-initialise after a re-render.
 */
(function () {
  var OPEN = "is-open";
  var PORTAL = "is-portal";
  var GAP = 4;

  function trigger(wrap) { return wrap.querySelector(".chw-usel__trigger"); }
  function native(wrap) { return wrap.querySelector(".cart-upsell-card__variant"); }

  // Once open the list is no longer inside the wrapper, so it is found by the
  // id the trigger already points at rather than by descending from the card.
  function listFor(wrap) {
    var t = trigger(wrap);
    var id = t && t.getAttribute("aria-controls");
    return (id && document.getElementById(id)) || wrap.querySelector(".chw-usel__list");
  }

  function place(wrap, list) {
    var r = wrap.getBoundingClientRect();
    list.style.width = r.width + "px";
    list.style.left = r.left + "px";
    // measure after the width is set, or a wrapped row reports the wrong height
    var h = list.offsetHeight;
    var below = window.innerHeight - r.bottom;
    var flip = below < h + GAP && r.top > h + GAP;
    list.style.top = (flip ? r.top - h - GAP : r.bottom + GAP) + "px";
  }

  var tracking = null;

  function track(wrap, list) {
    untrack();
    tracking = function () {
      if (!wrap.isConnected) { closeAll(null); return; }
      place(wrap, list);
    };
    // capture: the drawer scrolls in its own container, not the window
    window.addEventListener("scroll", tracking, true);
    window.addEventListener("resize", tracking);
  }

  function untrack() {
    if (!tracking) { return; }
    window.removeEventListener("scroll", tracking, true);
    window.removeEventListener("resize", tracking);
    tracking = null;
  }

  function open(wrap) {
    closeAll(wrap);
    var list = listFor(wrap);
    if (!list) { return; }
    wrap.classList.add(OPEN);
    trigger(wrap).setAttribute("aria-expanded", "true");

    document.body.appendChild(list);
    list.classList.add(PORTAL);
    list.hidden = false;
    place(wrap, list);
    track(wrap, list);

    var cur = list.querySelector('[aria-selected="true"]') || list.firstElementChild;
    // preventScroll: focusing inside a drawer otherwise yanks the page behind it
    if (cur) { cur.focus({ preventScroll: true }); }
  }

  function close(wrap, refocus) {
    var list = listFor(wrap);
    untrack();
    wrap.classList.remove(OPEN);
    var t = trigger(wrap);
    if (t) { t.setAttribute("aria-expanded", "false"); }
    if (list) {
      list.hidden = true;
      list.classList.remove(PORTAL);
      list.style.width = list.style.left = list.style.top = "";
      // If the drawer re-rendered while this was open its wrapper is gone, and
      // putting the list back would leave it orphaned in <body> forever.
      if (wrap.isConnected) { wrap.appendChild(list); }
      else if (list.parentNode === document.body) { list.parentNode.removeChild(list); }
    }
    if (refocus && t && wrap.isConnected) { t.focus({ preventScroll: true }); }
  }

  function closeAll(except) {
    var open = document.querySelectorAll(".chw-usel." + OPEN);
    Array.prototype.forEach.call(open, function (w) {
      if (w !== except) { close(w, false); }
    });
    // any list left in <body> by a re-render that happened mid-open
    var stray = document.querySelectorAll("body > .chw-usel__list." + PORTAL);
    Array.prototype.forEach.call(stray, function (l) {
      if (l.parentNode === document.body) { l.parentNode.removeChild(l); }
    });
  }

  function wrapOf(list) {
    var id = list.id;
    var t = id ? document.querySelector('[aria-controls="' + id + '"]') : null;
    return t ? t.closest(".chw-usel") : null;
  }

  function choose(wrap, opt) {
    var sel = native(wrap);
    if (!sel) { return; }
    sel.value = opt.getAttribute("data-value");

    var label = wrap.querySelector(".chw-usel__val");
    if (label) { label.textContent = opt.getAttribute("data-label") || ""; }

    var dot = trigger(wrap).querySelector(".chw-usel__dot");
    var sw = opt.getAttribute("data-swatch");
    if (dot) {
      if (sw) { dot.style.setProperty("--chw-usw", sw); dot.hidden = false; }
      else { dot.hidden = true; }
    }

    var list = listFor(wrap);
    Array.prototype.forEach.call(list.querySelectorAll(".chw-usel__opt"), function (o) {
      o.setAttribute("aria-selected", String(o === opt));
    });

    close(wrap, true);
    // one event, after the state is settled: the theme's handler and the image
    // swap both read the select, so they must not see it mid-update
    sel.dispatchEvent(new Event("change", { bubbles: true }));
  }

  document.addEventListener("click", function (e) {
    if (!e.target.closest) { return; }

    var t = e.target.closest(".chw-usel__trigger");
    if (t) {
      e.preventDefault();
      var wrap = t.closest(".chw-usel");
      if (wrap.classList.contains(OPEN)) { close(wrap, false); } else { open(wrap); }
      return;
    }

    var opt = e.target.closest(".chw-usel__opt");
    if (opt) {
      e.preventDefault();
      var w = wrapOf(opt.closest(".chw-usel__list"));
      if (w) { choose(w, opt); }
      return;
    }

    // the open list is outside the wrapper now, so it needs its own exemption
    if (!e.target.closest(".chw-usel") && !e.target.closest(".chw-usel__list")) {
      closeAll(null);
    }
  });

  document.addEventListener("keydown", function (e) {
    if (!e.target.closest) { return; }
    var list = e.target.closest(".chw-usel__list");
    var wrap = list ? wrapOf(list) : e.target.closest(".chw-usel");
    if (!wrap) {
      if (e.key === "Escape") { closeAll(null); }
      return;
    }
    var l = listFor(wrap);
    var opts = Array.prototype.slice.call(l.querySelectorAll(".chw-usel__opt"));
    var isOpen = wrap.classList.contains(OPEN);

    if (e.key === "Escape" && isOpen) { e.preventDefault(); close(wrap, true); return; }
    if (!isOpen && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault(); open(wrap); return;
    }
    if (!isOpen) { return; }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      var pick = e.target.closest(".chw-usel__opt");
      if (pick) { choose(wrap, pick); }
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      var at = opts.indexOf(e.target.closest(".chw-usel__opt"));
      var next = e.key === "ArrowDown" ? at + 1 : at - 1;
      if (next < 0) { next = opts.length - 1; }
      if (next >= opts.length) { next = 0; }
      opts[next].focus({ preventScroll: true });
    }
  });

  // A re-render or a closing drawer must not leave a list parked in <body>.
  ["cart-drawer:render", "cart-drawer:update", "cart-drawer:close", "cart-drawer:open"]
    .forEach(function (n) {
      document.addEventListener(n, function () { closeAll(null); });
    });
})();
